import BackToTop from "@bmi-digital/components/back-to-top";
import MicroCopy from "@bmi-digital/components/micro-copy";
import { graphql, navigate } from "gatsby";
import React, { useCallback, useMemo } from "react";
import { ErrorBoundary, withErrorBoundary } from "react-error-boundary";
import { GoogleReCaptchaProvider } from "react-google-recaptcha-v3";
import type { Brand } from "@bmi/payload-types";
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useConfig } from "../contexts/ConfigProvider";
import { BasketContextProvider } from "../contexts/SampleBasketContext";
import { getPathWithCountryCode } from "../utils/path";
import BrandProvider from "./BrandProvider";
import ErrorFallback from "./ErrorFallback";
import FallbackComponent from "./FallbackComponent";
import { Head } from "./Head";
import { generateGetMicroCopy } from "./MicroCopy";
import SignupBlock from "./SignupBlock";
import { SiteContextProvider } from "./Site";
import VisualiserProvider from "./Visualiser";
import CalculatorProvider from "./PitchedRoofCalculator";
import type { Visualiser } from "./Visualiser";
import type { HomePageData, SiteData as SiteData } from "./Site";
import type { SignupBlockData as SignupBlockData } from "./SignupBlock";
import type { MicroCopy as MicroCopyData } from "./MicroCopy";
import type { CountryNavigation, SampleOrder } from "../components/Header";
import type { HouseType } from "./visualiser/Types";
import type { GeneralErrorData } from "../templates/general-error";
import type { Product } from "../types/pim";
import type { SEOContentData as SEOContentData } from "./SEOContent";
import type { BreadcrumbData as BreadcrumbsData } from "./Breadcrumbs";
import type { CalculatorConfig } from "./pitched-roof-calculator/types";

export type PageData = {
  breadcrumbs: readonly BreadcrumbsData[];
  signupBlock: readonly SignupBlockData[];
  meta: SEOContentData | null;
  path: string;
};

type Children = React.ReactNode;

type Props = {
  brand?: Brand | null;
  children: Children;
  title: string;
  pageData: PageData;
  siteData: SiteData;
  microCopy: MicroCopyData[];
  pitchedRoofCalculatorConfig: CalculatorConfig | null;
  visualiserHouseTypes: HouseType[];
  homePage: HomePageData;
  generalError: GeneralErrorData;
  countryNavigation: CountryNavigation;
  sampleOrder: SampleOrder | null;
  visualiser: Visualiser | null;
  disableSearch?: boolean;
  variantCodeToPathMap?: Record<string, string>;
  ogImageUrl?: string | null;
  variantProduct?: Product;
  pageType?: string;
  pageId?: string;
  payloadPageId?: string;
};

const Page = ({
  brand,
  title,
  children,
  pageData,
  siteData,
  microCopy,
  pitchedRoofCalculatorConfig,
  visualiserHouseTypes,
  homePage,
  generalError,
  countryNavigation,
  sampleOrder,
  visualiser,
  disableSearch,
  variantCodeToPathMap,
  ogImageUrl,
  variantProduct,
  pageType,
  pageId,
  payloadPageId
}: Props) => {
  const {
    countryCode,
    footerMainNavigation,
    footerSecondaryNavigation,
    menuNavigation,
    menuUtilities,
    regions
  } = siteData;
  const { breadcrumbs, signupBlock, meta, path } = pageData;
  const {
    gatsbyReCaptchaKey,
    gatsbyReCaptchaNet,
    marketLocaleCode,
    visualizerAssetUrl
  } = useConfig();
  const reCaptchaNet = gatsbyReCaptchaNet === "true";

  const getMicroCopy = generateGetMicroCopy(microCopy);

  const microCopyContext = microCopy?.reduce(
    (carry, { key, value }) => ({
      ...carry,
      [key]: value
    }),
    {}
  );

  const siteContext = useMemo(
    () => ({
      node_locale: marketLocaleCode,
      countryCode,
      homePage,
      getMicroCopy,
      gatsbyReCaptchaKey,
      reCaptchaNet,
      pageId,
      payloadPageId
    }),
    [
      countryCode,
      gatsbyReCaptchaKey,
      getMicroCopy,
      marketLocaleCode,
      reCaptchaNet,
      homePage,
      pageId,
      payloadPageId
    ]
  );

  const seoTitle =
    variantProduct && variantProduct.seoTitle ? variantProduct.seoTitle : title;

  const onError = useCallback(
    () => navigate(getPathWithCountryCode(countryCode, "422")),
    [countryCode]
  );

  const fallbackRender = useCallback(
    () => (
      <ErrorFallback countryCode={countryCode} generalError={generalError} />
    ),
    [countryCode, generalError]
  );

  return (
    <div style={{ overflowX: "hidden", display: "contents" }}>
      <Head
        htmlAttributes={{ lang: marketLocaleCode }}
        title={seoTitle}
        defer={false}
        ogImageUrl={ogImageUrl}
        path={path}
        seo={meta}
        variantProduct={variantProduct}
        countryCode={countryCode}
        pageType={pageType}
        brandLogo={brand}
      />
      <SiteContextProvider value={siteContext}>
        <MicroCopy.Provider values={microCopyContext}>
          <GoogleReCaptchaProvider
            reCaptchaKey={gatsbyReCaptchaKey}
            useRecaptchaNet={reCaptchaNet}
            language={countryCode}
          >
            <ErrorBoundary fallbackRender={fallbackRender} onError={onError}>
              <BasketContextProvider>
                <Header
                  navigationData={menuNavigation}
                  utilitiesData={menuUtilities ? menuUtilities : null}
                  countryCode={countryCode}
                  lastNavigationLabel={
                    breadcrumbs?.[breadcrumbs.length - 1]?.label || undefined
                  }
                  activeLabel={
                    (breadcrumbs && breadcrumbs[0]?.label) || undefined
                  }
                  disableSearch={disableSearch}
                  countryNavigationIntroduction={
                    countryNavigation.countryNavigationIntroduction
                  }
                  regions={regions}
                  sampleOrder={sampleOrder}
                  variantCodeToPathMap={variantCodeToPathMap}
                />
                <VisualiserProvider
                  contentSource={visualizerAssetUrl}
                  variantCodeToPathMap={variantCodeToPathMap}
                  shareWidgetData={
                    visualiser ? visualiser.visualiserShareWidget : null
                  }
                  houseTypes={visualiserHouseTypes}
                >
                  <CalculatorProvider
                    onError={onError}
                    calculatorConfig={pitchedRoofCalculatorConfig}
                  >
                    <BrandProvider brand={brand}>{children}</BrandProvider>
                  </CalculatorProvider>
                </VisualiserProvider>
              </BasketContextProvider>
              {signupBlock ? <SignupBlock data={signupBlock[0]} /> : null}
              <Footer
                mainNavigation={
                  footerMainNavigation ? footerMainNavigation : null
                }
                secondaryNavigation={
                  footerSecondaryNavigation ? footerSecondaryNavigation : null
                }
              />
              <BackToTop accessibilityLabel="Back to the top" />
            </ErrorBoundary>
          </GoogleReCaptchaProvider>
        </MicroCopy.Provider>
      </SiteContextProvider>
    </div>
  );
};

export default withErrorBoundary(Page, {
  FallbackComponent: FallbackComponent
  // Possible to log errors with onError method
});

export const query = graphql`
  fragment PageFragment on PayloadPage {
    breadcrumbs {
      ...BreadcrumbItemFragment
    }
    path
  }

  fragment PayloadBrandLandingPageFragment on PayloadBrandLandingPage {
    ...PageFragment
    signupBlock {
      ...SignupBlockFragment
    }
    meta {
      ...SEOContentFragment
    }
  }

  fragment PayloadCookiePolicyPageFragment on PayloadCookiePolicyPage {
    ...PageFragment
    signupBlock {
      ...SignupBlockFragment
    }
    meta {
      ...SEOContentFragment
    }
  }

  fragment PayloadContactUsPageFragment on PayloadContactUsPage {
    ...PageFragment
    signupBlock {
      ...SignupBlockFragment
    }
    meta {
      ...SEOContentFragment
    }
  }

  fragment PayloadDocumentLibraryPageFragment on PayloadDocumentLibraryPage {
    ...PageFragment
    signupBlock {
      ...SignupBlockFragment
    }
    meta {
      ...SEOContentFragment
    }
  }

  fragment PayloadProductListerPageFragment on PayloadProductListerPage {
    ...PageFragment
    signupBlock {
      ...SignupBlockFragment
    }
    meta {
      ...SEOContentFragment
    }
  }

  fragment PayloadSimplePageFragment on PayloadSimplePage {
    ...PageFragment
    signupBlock {
      ...SignupBlockFragment
    }
    meta {
      ...SEOContentFragment
    }
  }

  fragment PayloadTrainingListerPageFragment on PayloadTrainingListerPage {
    ...PageFragment
    signupBlock {
      ...SignupBlockFragment
    }
    meta {
      ...SEOContentFragment
    }
  }

  fragment PayloadTrainingRegistrationPageFragment on PayloadTrainingRegistrationPage {
    breadcrumbs {
      ...BreadcrumbItemFragment
    }
    path
    signupBlock {
      ...SignupBlockFragment
    }
    meta {
      ...SEOContentFragment
    }
  }
`;
